import { Location } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Ability, subject } from '@casl/ability';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Project } from '../../../../../../../../shared/models';
import { REGISTER_GROUP_BYS, RegisterGroupBy, RegisterList, RegisterListState } from '../drawings-register/drawings-register.component';
import { SheetViewFilter } from '../drawing-sheet-viewer/drawing-sheet-viewer.component';
import { DrawingSheetListStatus } from '../../../../../../../../shared/models/drawing-sheet';
import { SHEET_TYPES, SheetType } from '../../../../../../../../shared/helpers/sheet-types';
import { AsBuiltStatus } from '../../../../../../../../shared/models/drawing-sheet';

type DrawingsView = 'register' | 'set' | 'viewer' | 'compare' | 'carry';

interface DrawingsParams {
  sheet: string | null;
  revision: string | null;
  set: string | null;
  compare: string | null;
  carry: string | null;
  carrySheet: string | null;
  /** An as-built mark to focus on the revision (the As-built tab's Open on sheet) */
  mark: string | null;
  /** The register's list (as-built / pending; sheets when unset) and the As-built tab's filters */
  list: string | null;
  abStatus: string | null;
  abSheet: string | null;
  abRev: string | null;
  abQ: string | null;
  /** W53: the Sheets list's Filter (N), several each ('-' in rDisc is "No discipline") */
  rDisc: string[] | null;
  rStatus: string[] | null;
  rType: string[] | null;
  /** W57: the Sheets list's Group by (discipline | type); None is the default and is not written */
  rGroup: string | null;
  /** W54: the sheet viewer's Filter (N): markup shown, record types, Show toggles ('-' is an empty list) */
  vMk: string[] | null;
  vRec: string[] | null;
  vShow: string[] | null;
  /** W56: the sheet in full screen ('1'); '0' keeps it out on a narrow screen, where full screen is the default */
  full: string | null;
}

const LIST_KEYS = ['list', 'abStatus', 'abSheet', 'abRev', 'abQ', 'rDisc', 'rStatus', 'rType', 'rGroup'] as const;
/** "No discipline" in the URL: an empty value would read as no parameter */
const NO_DISCIPLINE_PARAM = '-';
const SHEET_STATUSES: DrawingSheetListStatus[] = ['current', 'unpublished'];

const EMPTY_LIST_PARAM = '-';
const VIEW_MARKUP = ['shared', 'private', 'as_built'];
const VIEW_SHOW = ['mine', 'all_marks'];

/** A list for the URL: null for the default, '-' for an empty list */
function listParam(values: string[] | null): string[] | null {
  return values === null ? null : values.length ? values : [EMPTY_LIST_PARAM];
}

/** A list from the URL: null when absent (the default), [] for '-' */
function listFromParam(value: unknown): string[] | null {
  const list = paramList(value);
  return !list.length ? null : list.filter(v => v !== EMPTY_LIST_PARAM);
}

/** A repeated query param as a list (one value comes as a string) */
function paramList(value: unknown): string[] {
  return (Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]).map(String);
}
const LISTS: RegisterList[] = ['sheets', 'as-built', 'pending'];
/** The projects wrapper's phone breakpoint: the sheet opens in full screen there */
const NARROW_QUERY = '(max-width: 768px)';
const AS_BUILT_STATUSES: AsBuiltStatus[] = ['open', 'reviewed', 'rejected', 'incorporated'];

@Component({
  selector: 'app-drawings-wrapper',
  templateUrl: './drawings-wrapper.component.html',
  styleUrls: ['./drawings-wrapper.component.scss'],
  standalone: false,
})
export class DrawingsWrapperComponent implements OnInit, OnDestroy {
  @Input() project: Project;
  projectId: string;
  view: DrawingsView = 'register';
  /** W54: the sheet viewer's Filter (N) from the URL */
  viewFilter: SheetViewFilter | null = null;
  /** W56: the sheet viewer's full screen, from the URL (the default on a narrow screen) */
  fullScreen = false;
  private fullParam: string | null = null;
  /** Full screen was opened here (a new history entry): leaving it goes back, so browser back and ✕ agree */
  private fullPushed = false;
  /** Review M1: the Filter (N) as changed in full screen, put back on the entry ✕ / Esc returns to */
  private filterToRestore: Record<string, string[] | null> | null = null;
  private readonly location = inject(Location);
  sheetUuid: string | null = null;
  revisionUuid: string | null = null;
  setUuid: string | null = null;
  /** Compare: the older revision shown beside `revisionUuid` */
  compareUuid: string | null = null;
  /** Carry-forward review for every sheet of a published set, or for one sheet */
  carrySetUuid: string | null = null;
  carrySheetUuid: string | null = null;
  markUuid: string | null = null;
  /** The register's list and filters from the URL */
  listState: Partial<RegisterListState> = {};
  /** The register's last list query, which Back from a sheet, set or carry-forward returns to */
  private registerParams: Partial<DrawingsParams> = {};
  canConfigure = false;
  canPublish = false;
  private readonly onDestroy = new Subject<void>();

  constructor(private route: ActivatedRoute, private router: Router, private ability: Ability) {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.canConfigure = this.ability.can('configure', subject('ProjectDrawing', { project_id: this.projectId }));
    this.canPublish = this.ability.can('publish', subject('ProjectDrawing', { project_id: this.projectId }));
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.onDestroy)).subscribe((qp) => {
      if (qp.tab !== 'drawings') { return; }
      this.sheetUuid = qp.sheet || null;
      this.revisionUuid = qp.revision || null;
      this.setUuid = qp.set || null;
      this.compareUuid = qp.compare || null;
      this.carrySetUuid = qp.carry || null;
      this.carrySheetUuid = qp.carrySheet || null;
      this.markUuid = qp.mark || null;
      // A hand-edited list or status is ignored; an unknown sheet just lists nothing.
      this.listState = {
        list: LISTS.includes(qp.list) ? qp.list : 'sheets',
        status: AS_BUILT_STATUSES.includes(qp.abStatus) ? qp.abStatus : '',
        sheet: qp.abSheet || '',
        // A revision belongs to a sheet: without one it is ignored
        revision: qp.abSheet ? qp.abRev || '' : '',
        q: qp.abQ || '',
        disciplines: paramList(qp.rDisc).map(d => (d === NO_DISCIPLINE_PARAM ? '' : d)),
        statuses: paramList(qp.rStatus).filter((s): s is DrawingSheetListStatus => SHEET_STATUSES.includes(s as DrawingSheetListStatus)),
        types: paramList(qp.rType).filter((t): t is SheetType => SHEET_TYPES.some(o => o.value === t)),
        group: REGISTER_GROUP_BYS.includes(qp.rGroup) ? (qp.rGroup as RegisterGroupBy) : 'none',
      };
      if (this.carrySetUuid || this.carrySheetUuid) {
        this.view = 'carry';
      } else if (this.revisionUuid && this.compareUuid) {
        this.view = 'compare';
      } else {
        this.view = this.revisionUuid || this.sheetUuid ? 'viewer' : this.setUuid ? 'set' : 'register';
      }
      // W54: the sheet viewer's Filter (N); unknown values are dropped
      this.viewFilter = {
        markup: listFromParam(qp.vMk)?.filter(v => VIEW_MARKUP.includes(v)) ?? null,
        records: listFromParam(qp.vRec),
        show: (listFromParam(qp.vShow) ?? []).filter(v => VIEW_SHOW.includes(v)),
      };
      this.fullParam = qp.full === '1' || qp.full === '0' ? qp.full : null;
      this.fullScreen = this.view === 'viewer' && (this.fullParam ? this.fullParam === '1' : this.isNarrow());
      if (!this.fullScreen) {
        this.fullPushed = false;
        if (this.filterToRestore) {
          const queryParams = this.filterToRestore;
          this.filterToRestore = null;
          if (this.view === 'viewer') {
            this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge', queryParams });
          }
        }
      }
      if (this.view === 'register') {
        this.registerParams = Object.fromEntries(LIST_KEYS.map(k => [k, qp[k] || null]));
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  openSheet(uuid: string): void { this.go({ sheet: uuid }); }
  openRevision(uuid: string): void {
    // Another revision from full screen stays in full screen; ✕ then just leaves it (no going back to the old revision).
    // Left on a narrow screen (full=0), it stays out (review M2).
    this.fullPushed = false;
    this.go({ revision: uuid, full: this.fullParam });
  }
  openMark(e: { revision: string; mark: string }): void { this.go({ revision: e.revision, mark: e.mark }); }
  openSet(uuid: string): void { this.go({ set: uuid }); }
  openCompare(e: { revision: string; against: string }): void { this.go({ revision: e.revision, compare: e.against }); }
  /** Set review's Preview changes: compare the draft page (revision) with the issued revision it replaces; ?set brings Back there */
  openPreviewDiff(e: { revision: string; against: string }): void { this.go({ revision: e.revision, compare: e.against, set: this.setUuid }); }
  /** Compare's back: to the set review it was opened from, else to the sheet */
  backFromCompare(): void {
    if (this.setUuid) {
      this.openSet(this.setUuid);
    } else {
      this.openRevision(this.revisionUuid);
    }
  }
  openCarryForSet(uuid: string): void { this.go({ carry: uuid }); }
  openCarryForSheet(uuid: string): void { this.go({ carrySheet: uuid }); }
  backToRegister(): void { this.go(this.registerParams); }

  /** The register's list or As-built filters changed: keep them in the URL without a new history entry */
  onListState(state: RegisterListState): void {
    const params: Partial<DrawingsParams> = {
      list: state.list === 'sheets' ? null : state.list,
      abStatus: state.status || null,
      abSheet: state.sheet || null,
      abRev: state.sheet && state.revision ? state.revision : null,
      abQ: state.q || null,
      rDisc: state.disciplines?.length ? state.disciplines.map(d => d || NO_DISCIPLINE_PARAM) : null,
      rStatus: state.statuses?.length ? [...state.statuses] : null,
      rType: state.types?.length ? [...state.types] : null,
      rGroup: state.group && state.group !== 'none' ? state.group : null,
    };
    this.registerParams = params;
    this.go(params, true);
  }

  /** The viewer's Filter (N) changed: kept in the URL without a new history entry (another sheet or revision resets it) */
  onViewFilter(state: SheetViewFilter): void {
    const queryParams = { vMk: listParam(state.markup), vRec: listParam(state.records), vShow: state.show.length ? state.show : null };
    // The full-screen entry is popped on ✕ / Esc: the entry under it gets this filter then (review M1)
    this.filterToRestore = this.fullPushed ? queryParams : null;
    this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge', queryParams });
  }

  /** W56: Expand adds a history entry (browser back leaves full screen); ✕ / Esc go back to it, or drop the flag after a reload */
  onFullScreen(on: boolean): void {
    if (on) {
      this.fullPushed = true;
      this.router.navigate([], { relativeTo: this.route, queryParamsHandling: 'merge', queryParams: { full: '1' } });
      return;
    }
    if (this.fullPushed) {
      this.fullPushed = false;
      this.location.back();
      return;
    }
    this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParamsHandling: 'merge', queryParams: { full: this.isNarrow() ? '0' : null } });
  }

  /** A phone-sized window (overridable in specs) */
  isNarrow(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia?.(NARROW_QUERY).matches;
  }

  private go(params: Partial<DrawingsParams>, replaceUrl = false): void {
    // Another view starts from its own filter
    this.filterToRestore = null;
    const all: DrawingsParams = {
      sheet: null, revision: null, set: null, compare: null, carry: null, carrySheet: null, mark: null,
      list: null, abStatus: null, abSheet: null, abRev: null, abQ: null, rDisc: null, rStatus: null, rType: null, rGroup: null,
      vMk: null, vRec: null, vShow: null, full: null, ...params,
    };
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab: 'drawings', ...all }, queryParamsHandling: 'merge', ...(replaceUrl ? { replaceUrl } : {}) });
  }
}
